import React, { useContext, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SHOP_PATHS, Url } from '@wix/da-url';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
import EmptyWatchedGroups from './_partials/EmptyWatchedGroups';
import NetworkBarLink from './_partials/NetworkBarLink';
import HomeIcon from '@wix/da-ds/pkg/Icons/24x24/Home';
import Eyecon from '@wix/da-ds/pkg/Icons/24x24/FollowingWatch';
import ShopIcon from '@wix/da-ds/pkg/Icons/24x24/Shop';
import PopularIcon from '@wix/da-ds/pkg/Icons/24x24/Popular';
import DailyDeviationIcon from '@wix/da-ds/pkg/Icons/24x24/DailyDeviation';
import TopicsIcon from '@wix/da-ds/pkg/Icons/24x24/Topics';
import NewestIcon from '@wix/da-ds/pkg/Icons/24x24/Newest';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import RecommendedIcon from '@wix/da-ds/pkg/Icons/Recommended';
import AddToGroupFill from '@wix/da-ds/pkg/Icons/16x16/AddToGroupFill';
import GiveFeedbackIcon from '@wix/da-ds/pkg/Icons/24x24/GiveFeedback';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import { watchedGroupsStreamId, recommendedGroupsStreamId, NETWORKBAR_FEEDBACK_URL, } from './redux/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { useScrollDescendantIntoView } from './hooks/useScrollDescendantIntoView';
import FoldableGroupList from './_partials/FoldableGroupList';
import NetworkBarItemGroup from './_partials/NetworkBarItemGroup';
import sOriginal from './NetworkBar.scss';
import sNewSiteHeader from './NetworkBar.new_site_header.scss';
var menuItemIconMap = {
    home: HomeIcon,
    watch: Eyecon,
    shop: ShopIcon,
    dailyDeviations: DailyDeviationIcon,
    topics: TopicsIcon,
    popular: PopularIcon,
    new: NewestIcon,
    dreamup: DreamUpIcon,
};
var LINKS_SHOWN_ONLY_ON_MOBILE = ['shop', 'dreamup'];
var NetworkBar = function (_a) {
    var id = _a.id, initialFetch = _a.initialFetch, _b = _a.isVisible, isVisible = _b === void 0 ? true : _b, isCollapsed = _a.isCollapsed, selectedItem = _a.selectedItem, showWatchedGroupsEmptyState = _a.showWatchedGroupsEmptyState, showRecommendedGroups = _a.showRecommendedGroups, className = _a.className, isInitialized = _a.isInitialized, mainNavItems = _a.mainNavItems, canSeeOldWatch = _a.canSeeOldWatch;
    var _c = useState(true), hideContent = _c[0], setHideContent = _c[1];
    var scrollableRef = useRef(null);
    var t = useTranslation().t;
    var isMobile = useContext(MobileContext);
    var isLoggedIn = useContext(SessionContext).isLoggedIn;
    var features = useEnabledFlagList();
    var isNewSiteHeader = features.includes('new_site_header');
    var shop_default_commissions = features.includes('shop_default_commissions');
    var s = isNewSiteHeader ? sNewSiteHeader : sOriginal;
    var tabIndex = isVisible ? 0 : -1;
    var mainNavItemsFiltered = mainNavItems.filter(function (item) {
        return isMobile || !LINKS_SHOWN_ONLY_ON_MOBILE.includes(item.key);
    });
    useScrollDescendantIntoView(scrollableRef, s['selected-item'], isInitialized);
    useEffect(function () {
        if (!isInitialized && isVisible) {
            initialFetch();
        }
        setHideContent(false);
    }, [initialFetch, isInitialized, isVisible]);
    return (React.createElement(BiLoggerContextProvider, { value: { is_expanded: !isCollapsed ? 1 : 0 } },
        React.createElement("div", { className: classnames(!isMobile && s['scroll-wrapper'], isCollapsed && s['collapsed'], hideContent && s['hidden'], className), ref: scrollableRef },
            React.createElement(Nav, { className: s['root'], id: id },
                React.createElement(VisuallyHidden, null,
                    React.createElement(Heading, null, t('common.networkBar'))),
                React.createElement("ul", null, mainNavItemsFiltered.map(function (item) {
                    var _a, _b;
                    if (!item ||
                        (item.restrictLoggedIn && !isLoggedIn) ||
                        item.key === 'new' ||
                        (item.withFeature && !features.includes(item.withFeature))) {
                        return null;
                    }
                    var url = shop_default_commissions && item.key === 'shop'
                        ? Url.shopLink(SHOP_PATHS.COMMISSIONS)
                        : item.url;
                    return (React.createElement("li", { key: item.key },
                        React.createElement(NetworkBarLink, { label: item.key, url: url, icon: menuItemIconMap[item.key], tabIndex: tabIndex, isCollapsed: !isMobile && isCollapsed, isSelected: selectedItem === ((_a = item.pageType) !== null && _a !== void 0 ? _a : item.key), className: classnames(selectedItem === ((_b = item.pageType) !== null && _b !== void 0 ? _b : item.key) &&
                                s['selected-item']), hasNewContent: item.hasNewContent, betaBadge: item.betaBadge, showTooltip: true, menuItems: isMobile && item.key === 'watch' && canSeeOldWatch
                                ? [
                                    {
                                        label: t('networkBar.navItems.watch.actions.oldWatch'),
                                        link: Url.watchLink(),
                                        biData: {
                                            evid: BiEventType.NETWORK_BAR_3DOTS_SELECT,
                                            sectionname: item.key,
                                            indicator: item.hasNewContent ? 1 : 0,
                                            menu_item: 'oldWatch',
                                        },
                                    },
                                ]
                                : undefined })));
                })),
                showWatchedGroupsEmptyState && !isCollapsed && (React.createElement("div", { className: s['group'] },
                    React.createElement(BiLoggerContextProvider, { value: { sectionname: 'groups' } },
                        React.createElement(NetworkBarItemGroup, { title: t('networkBar.subheader.groups'), icon: AddToGroupFill, isCollapsed: isCollapsed },
                            React.createElement(EmptyWatchedGroups, null))))),
                !showWatchedGroupsEmptyState && (React.createElement(BiLoggerContextProvider, { value: { sectionname: 'groups' } },
                    React.createElement(FoldableGroupList, { sectionType: "watched_groups", streamId: watchedGroupsStreamId, tabIndex: tabIndex, selectedItem: selectedItem, isCollapsed: isCollapsed, selectedItemClassName: s['selected-item'], icon: AddToGroupFill, title: t('networkBar.subheader.groups'), toggleAriaLabel: t('networkBar.subheader.groups'), className: s['group'] }))),
                showRecommendedGroups && (React.createElement(BiLoggerContextProvider, { value: { sectionname: 'recommended groups' } },
                    React.createElement(FoldableGroupList, { sectionType: "recommended_groups", streamId: recommendedGroupsStreamId, tabIndex: tabIndex, selectedItem: selectedItem, isCollapsed: isCollapsed, selectedItemClassName: s['selected-item'], className: s['group'], icon: RecommendedIcon, title: t('networkBar.subheader.recommendedGroups'), toggleAriaLabel: t('networkBar.subheader.recommendedGroups') }))),
                isLoggedIn && (React.createElement("div", { className: s['group'] },
                    React.createElement(NetworkBarLink, { label: "feedback", url: NETWORKBAR_FEEDBACK_URL, icon: GiveFeedbackIcon, tabIndex: tabIndex, isCollapsed: !isMobile && isCollapsed })))))));
};
NetworkBar.displayName = 'NetworkBar';
export default NetworkBar;
//# sourceMappingURL=NetworkBar.js.map